/**
 * Headline statistic tabs for the rating plots (decision 20, RND-2753):
 * "Average" | "Top N" (N = boxSize: 2 on 4-7 point scales, 3 on 10-11), or
 * "Average" | "NPS" on an nps_like read (`stat_tab_nps`).
 *
 * Display only. Every figure behind a tab is Python's (`PlotStatViews`,
 * from `rating_statistics.py`); this component never computes a share. A tab
 * whose figure is absent is hidden (Top N exists only on discrete rating
 * scales; never on binary, 0-100 or continuous), and a tab with no label is
 * omitted, never filled in English. With one tab left there is nothing to
 * choose, so nothing is drawn.
 *
 * Controlled: the payload stamps `defaultView`, the caller holds `activeView`
 * and passes `onViewChange`. Shared by diverging-likert (P) and the heat-grid
 * / radar plots (Q).
 *
 * Plot code never imports the frontend's `@/lib`.
 */

import React, { useRef } from 'react'

import { PlotStatView, PlotStatViews } from '@digital-twins/slide-schemas/generated/plot-types'

export interface StatTabLabels {
  average?: string | null
  /** The `stat_tab_top_n` template ("Top {n}"), filled with `stats.boxSize`. */
  topBoxes?: string | null
  nps?: string | null
}

export interface StatTabsProps {
  /** One read's figures: the tabs are the views it offers. */
  stats?: PlotStatViews
  /**
   * A card's tabs, in place of one read's `stats`: the views Python stamped
   * as shared by every row on the card (`KpiData.views`, the lift card's
   * rows). Drawn in tab order. The Top N label is filled by the caller
   * (a card may mix Top 2 and Top 3 rows).
   */
  views?: Array<PlotStatView>
  activeView: PlotStatView
  labels: StatTabLabels
  onViewChange?: (view: PlotStatView) => void
  /** Prefix for each tab's `data-testid` (`stat-tab-average`, ...). */
  testIdPrefix?: string
}

interface StatTab {
  view: PlotStatView
  label: string
  testId: string
}

function hasFigure(value: number | null | undefined): boolean {
  return typeof value === 'number' && Number.isFinite(value)
}

/** The views a read offers, in tab order: Average, then NPS or Top N. */
export function availableStatViews(stats: PlotStatViews): Array<PlotStatView> {
  const views: Array<PlotStatView> = ['mean']
  if (hasFigure(stats.nps)) {
    views.push('nps')
  } else if (hasFigure(stats.topBoxes) && hasFigure(stats.boxSize)) {
    views.push('topBoxes')
  }
  return views
}

/** The view to draw: the requested one when the read offers it, else Average. */
export function resolveStatView(stats: PlotStatViews, requested: PlotStatView | null | undefined): PlotStatView {
  if (requested && availableStatViews(stats).includes(requested)) {
    return requested
  }
  return 'mean'
}

const TAB_ORDER: Array<PlotStatView> = ['mean', 'topBoxes', 'nps']

/** The views a card offers, in tab order (Average first). */
function orderedViews(views: Array<PlotStatView>): Array<PlotStatView> {
  return TAB_ORDER.filter((view) => views.includes(view))
}

function statTabs(
  offered: Array<PlotStatView>,
  boxSize: number | null | undefined,
  labels: StatTabLabels,
  testIdPrefix: string,
): Array<StatTab> {
  const topBoxesLabel =
    labels.topBoxes && boxSize != null ? labels.topBoxes.replace('{n}', String(boxSize)) : labels.topBoxes
  const byView: Record<PlotStatView, { label: string | null | undefined; testId: string }> = {
    mean: { label: labels.average, testId: `${testIdPrefix}average` },
    topBoxes: { label: topBoxesLabel, testId: `${testIdPrefix}top-boxes` },
    nps: { label: labels.nps, testId: `${testIdPrefix}nps` },
  }
  return offered
    .map((view) => ({ view, label: (byView[view].label || '').trim(), testId: byView[view].testId }))
    .filter((tab) => tab.label.length > 0 && !(tab.view === 'topBoxes' && tab.label.includes('{n}')))
}

export function StatTabs(props: StatTabsProps) {
  const { stats, views, activeView, labels, onViewChange, testIdPrefix = 'stat-tab-' } = props

  const tabRefs = useRef<Array<HTMLButtonElement | null>>([])

  const offered = views ? orderedViews(views) : stats ? availableStatViews(stats) : []
  const tabs = statTabs(offered, views ? null : stats?.boxSize, labels, testIdPrefix)
  const active = offered.includes(activeView) ? activeView : 'mean'

  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
      return
    }
    event.preventDefault()
    const step = event.key === 'ArrowRight' ? 1 : -1
    const nextIndex = (index + step + tabs.length) % tabs.length
    // Roving focus: the arrow moves focus to the next tab and selects it.
    tabRefs.current[nextIndex]?.focus()
    onViewChange?.(tabs[nextIndex].view)
  }

  if (tabs.length < 2) {
    return null
  }

  return (
    <div
      role="tablist"
      data-testid={`${testIdPrefix}list`}
      style={{ display: 'inline-flex', gap: 4, flexShrink: 0 }}
    >
      {tabs.map((tab, index) => {
        const isSelected = tab.view === active
        return (
          <button
            key={tab.view}
            ref={(node) => {
              tabRefs.current[index] = node
            }}
            type="button"
            role="tab"
            data-testid={tab.testId}
            data-selected={isSelected ? 'true' : 'false'}
            aria-selected={isSelected}
            tabIndex={isSelected ? 0 : -1}
            onClick={() => onViewChange?.(tab.view)}
            onKeyDown={(event) => onKeyDown(event, index)}
            style={{
              border: isSelected ? '1px solid var(--slide-chart-1)' : '1px solid var(--slide-border-soft)',
              background: isSelected ? 'var(--slide-accent-soft)' : 'var(--slide-bg)',
              borderRadius: 999,
              padding: '4px 10px',
              boxSizing: 'border-box',
              lineHeight: 1.2,
              fontSize: 12,
              color: 'var(--slide-text-primary)',
              cursor: onViewChange ? 'pointer' : 'default',
              whiteSpace: 'nowrap',
            }}
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}
